import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { base64Kit } from '@/utils/base64-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import type { IChartLayer, ILayerBehavior } from '@/version-types';
import { prepareChartLayerDocumentFonts } from '../font-measurement';
import { renderLayerContentToCanvas } from '../render/render-to-canvas';
import { renderLayerToPPT } from '../render/render-to-ppt';
import { renderLayerToStage } from '../render/render-to-stage';
import { getChartLayerScreenshotUrlCode, initializeChartLayerScreenshotUrl } from '../utils/screenshot';

type Behaviors = Pick<
    ILayerBehavior,
    'onBeforeAddOrUpdate' | 'onBeforeSaveAsync' | 'savePPTLayerAsync' | 'getResultCanvasAsync' | 'getLayerImageAsync'
>;

export const createSaveBehaviors = (frameworkIdentity: string) => {
    const prepareDocumentFonts = (layer: IChartLayer) =>
        prepareChartLayerDocumentFonts(getFramework(frameworkIdentity).service, layer);

    const behaviors: Behaviors = {
        onBeforeAddOrUpdate(layer) {
            initializeChartLayerScreenshotUrl(layer as IChartLayer, getLocaleStore().locale);
        },

        async getLayerImageAsync(layer, globalAlpha = false) {
            const chartLayer = layer as IChartLayer;
            await prepareDocumentFonts(chartLayer);
            return renderLayerContentToCanvas(chartLayer, globalAlpha);
        },

        async onBeforeSaveAsync(_layer) {
            const layer = _layer as IChartLayer;
            deleteExtraInfo(layer);
            const code = await getChartLayerScreenshotUrlCode(frameworkIdentity, layer);
            layer.data.screenshotUrl = code ? await base64Kit.getRemoteUrlAsync(code) : '';
        },

        async getResultCanvasAsync(layer, resultSize) {
            const chartLayer = layer as IChartLayer;
            await prepareDocumentFonts(chartLayer);
            return renderLayerToStage(chartLayer, resultSize);
        },

        async savePPTLayerAsync(slide, _layer, _stageWidth, _stageHeight, _watermark, outFontFamilyArr) {
            const layer = _layer as IChartLayer;
            await prepareDocumentFonts(layer);
            return renderLayerToPPT(slide, layer, outFontFamilyArr);
        },
    };

    return behaviors;
};
